import { z } from "zod";
import { authHeaders } from "@/lib/api";

const decimal = z.string().regex(/^-?\d+(?:\.\d+)?$/);
const nonnegativeInt = z.number().int().nonnegative().safe();
const invoiceSchema = z.object({
  invoice_number: z.string().min(1),
  invoice_date: z.string().regex(/^\d{4}-\d{2}-\d{2}(?:T.*)?$/).nullable(),
  total_tvac: decimal.refine((value) => Number.isFinite(Number(value))).nullable(),
});
const lineSchema = z.object({
  seq: z.number().int(), product_id: z.number().int().nullable(),
  ean: z.string().nullable(), label: z.string().nullable(),
  quantity: decimal.nullable(), serial_number: z.string().nullable(),
});
const principalPart = z.string().min(1).regex(/^[^\s\u0000-\u001f\u007f]+$/);
const ownerSchema = z.object({ sub: principalPart.max(255), issuer: principalPart }).strict();
export type PortalInvoiceOwner = Readonly<z.infer<typeof ownerSchema>>;
const invoicesSchema = z.object({ owner: ownerSchema, rows: z.array(invoiceSchema).max(50), total: nonnegativeInt, offset: nonnegativeInt });
const linesSchema = z.object({ owner: ownerSchema, invoice: invoiceSchema, lines: z.array(lineSchema).max(100), total: nonnegativeInt, offset: nonnegativeInt });
export type PortalInvoice = z.infer<typeof invoiceSchema>;
export type PortalInvoiceLine = z.infer<typeof lineSchema>;
export type PortalInvoicesPage = z.infer<typeof invoicesSchema>;
export type PortalInvoiceLinesPage = z.infer<typeof linesSchema>;
export class CustomerInvoicesError extends Error {
  constructor(public readonly status: number) { super(`Invoice request failed (${status})`); }
}

async function get<T>(path: string, signal?: AbortSignal): Promise<T> {
  const response = await fetch(path, {
    method: "GET", headers: authHeaders(), credentials: "same-origin", cache: "no-store",
    ...(signal ? { signal } : {}),
  });
  if (!response.ok) throw new CustomerInvoicesError(response.status);
  return response.json() as Promise<T>;
}

// Expected owner is a local response-binding check, NEVER an authorization scope.
// Parsing snapshots it before the request; compare literal server values, without normalization.
export async function fetchCustomerInvoices(owner: PortalInvoiceOwner, offset = 0, signal?: AbortSignal): Promise<PortalInvoicesPage> {
  const expected = ownerSchema.parse(owner);
  nonnegativeInt.parse(offset);
  const page = invoicesSchema.parse(await get<unknown>(`/api/portal/invoices?limit=50&offset=${offset}`, signal));
  if (page.owner.sub !== expected.sub || page.owner.issuer !== expected.issuer) throw new Error("Unexpected invoice owner");
  if (page.offset !== offset) throw new Error("Unexpected invoice page");
  return page;
}
export async function fetchCustomerInvoiceLines(owner: PortalInvoiceOwner, invoiceNumber: string, offset = 0, signal?: AbortSignal): Promise<PortalInvoiceLinesPage> {
  const expected = ownerSchema.parse(owner);
  nonnegativeInt.parse(offset);
  const page = linesSchema.parse(await get<unknown>(`/api/portal/invoices/${encodeURIComponent(invoiceNumber)}/lines?limit=100&offset=${offset}`, signal));
  if (page.owner.sub !== expected.sub || page.owner.issuer !== expected.issuer) throw new Error("Unexpected invoice owner");
  if (page.offset !== offset || page.invoice.invoice_number !== invoiceNumber) throw new Error("Unexpected invoice detail");
  return page;
}
